'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { MediaMachineTypes } from './MediaMachineAI';
import styles from './MediaMachine.module.sass';
import MediaMachineConnections from './MediaMachineConnections';
import { MediaMachineCameraCategory } from './MediaMachineCategory';
import { v4 as uuidv4 } from 'uuid';
import Icon from '@mdi/react';
import { mdiPlusCircle } from '@mdi/js';
import EmojiText from '@/components/EmojiText/EmojiText';
import MediaMachinePermissionManager from './MediaMachinePermissionManager';
import { measure } from '@/components/Analytics/Analytics';

export type MediaMachineCategory = {
    id: string;
    name: string;
    color: string;
    samples: number;
};

const MediaMachine = ({
    machineTypeId,
    isEmbedded = false,
    preferredCategoryNames = ['', ''],
    onMinimumSamplesRecorded = () => undefined,
    onReady = () => undefined,
}: {
    machineTypeId: keyof typeof MediaMachineTypes;
    isEmbedded?: boolean;
    preferredCategoryNames?: string[];
    onMinimumSamplesRecorded?: () => void;
    onReady?: () => void;
}) => {
    const frameRef = useRef<HTMLIFrameElement>(null);

    const [isReady, setIsReady] = useState(false);
    const [, setIsLoading] = useState(false);

    const [categories, setCategories] = useState<MediaMachineCategory[]>([]);
    const [classifiedCategory, setClassifiedCategory] =
        useState<MediaMachineCategory | null>(null);

    return (
        <div
            className={`${styles.mediamachine} ${
                isEmbedded
                    ? styles['mediamachine--embedded']
                    : styles['mediamachine--full']
            }`}
        >
            <MediaMachinePermissionManager
                canSkip={isEmbedded}
                onNoPermission={onMinimumSamplesRecorded}
            >
                <MediaMachineFrame
                    machineTypeId={machineTypeId}
                    isEmbedded={isEmbedded}
                    frameRef={frameRef}
                    onMinimumSamplesRecorded={onMinimumSamplesRecorded}
                    onLoading={() => {
                        setIsLoading(true);
                    }}
                    onReady={() => {
                        setIsReady(true);
                        onReady();
                    }}
                    categories={categories}
                    classifiedCategory={classifiedCategory}
                    setClassifiedCategory={setClassifiedCategory}
                />
            </MediaMachinePermissionManager>
            {isReady && (
                <MediaMachineCategories
                    isEmbedded={isEmbedded}
                    frameRef={frameRef}
                    preferredCategoryNames={preferredCategoryNames}
                    categories={categories}
                    setCategories={setCategories}
                    classifiedCategory={classifiedCategory}
                />
            )}
        </div>
    );
};

const MediaMachineFrame = ({
    machineTypeId,
    isEmbedded,
    frameRef,
    onMinimumSamplesRecorded,
    onLoading,
    onReady,
    categories,
    classifiedCategory,
    setClassifiedCategory,
}: {
    machineTypeId: keyof typeof MediaMachineTypes;
    isEmbedded?: boolean;
    frameRef: React.RefObject<HTMLIFrameElement | null>;
    onMinimumSamplesRecorded: () => void;
    onLoading: () => void;
    onReady: () => void;
    categories: MediaMachineCategory[];
    classifiedCategory: MediaMachineCategory | null;
    setClassifiedCategory: (category: MediaMachineCategory | null) => void;
}) => {
    const [frameState, setFrameState] = useState<
        'frame_loading' | 'ai_loading' | 'ready'
    >('frame_loading');

    const onMachineFrameMessage = useCallback(
        (event: MessageEvent) => {
            if (event.data?._hetklokhuisaistudio) {
                switch (event.data.type) {
                    case 'ai.loading':
                        setFrameState('ai_loading');
                        onLoading();
                        break;
                    case 'ai.loaded':
                        setFrameState('ready');
                        onReady();
                        break;
                    case 'ai.classified': {
                        setClassifiedCategory(
                            categories.find(
                                (category) =>
                                    category.id == event.data.classInfo.id,
                            ) ?? null,
                        );
                        const totalSamplesRecorded = (
                            Object.values(
                                event.data.sampleCount as {
                                    [key: string]: number;
                                },
                            ) || []
                        ).reduce((a, b) => a + b, 0);
                        if (totalSamplesRecorded == 10) {
                            setTimeout(onMinimumSamplesRecorded, 10000);
                        }
                        break;
                    }
                }
            }
        },
        [setClassifiedCategory, categories, onMinimumSamplesRecorded],
    );

    useEffect(() => {
        if (frameRef.current) {
            window.addEventListener('message', onMachineFrameMessage);
            if (!frameRef.current.src) {
                frameRef.current.src = `/embedded/media-machine/${machineTypeId}/index.html`;
                frameRef.current.focus();
            }
        }

        return () => {
            window.removeEventListener('message', onMachineFrameMessage);
        };
    }, [frameRef.current, setClassifiedCategory, categories]);

    return (
        <div
            className={`${styles['mediamachine__frame']} ${
                classifiedCategory
                    ? `${styles['mediamachine__frame--classified']} bg-${classifiedCategory.color} border-${classifiedCategory.color}`
                    : ''
            } ${
                frameState == 'ready'
                    ? styles['mediamachine__frame--ready']
                    : ''
            }`}
        >
            <iframe
                ref={frameRef}
                title="Media preview"
                className={styles['mediamachine__frame__iframe']}
            />
            {!!classifiedCategory && (
                <div
                    className={`${styles['mediamachine__frame__classifiedname']} bg-${classifiedCategory.color}`}
                >
                    AI denkt nu{' '}
                    {classifiedCategory.name.length > 0 ? (
                        isEmbedded ? (
                            <span
                                className={
                                    styles[
                                        'mediamachine__frame__classifiedname__emoji'
                                    ]
                                }
                            >
                                <EmojiText>{classifiedCategory.name}</EmojiText>
                            </span>
                        ) : (
                            classifiedCategory.name
                        )
                    ) : (
                        'Categorie'
                    )}
                </div>
            )}
        </div>
    );
};

enum MediaMachineColors {
    PRIMARY = 'primary-text-lg-aa',
    SECONDARY = 'secondary-text-lg-aa',
}

const createNewCategory = (
    categories: MediaMachineCategory[],
    preferredName: string,
    plannedIndex = -1,
) => {
    const index = plannedIndex == -1 ? categories.length : plannedIndex;
    let color =
        Object.values(MediaMachineColors)[
            index % Object.values(MediaMachineColors).length
        ];

    if (categories.length > 0) {
        const lastCategory = categories[categories.length - 1];
        const lastColorIndex = Object.values(MediaMachineColors).indexOf(
            lastCategory.color as MediaMachineColors,
        );
        color =
            Object.values(MediaMachineColors)[
                (lastColorIndex + 1) % Object.values(MediaMachineColors).length
            ];

        // If the color is already used, use the first available color
        if (categories.find((c) => c.color == color)) {
            const colorsLeft = Object.values(MediaMachineColors).filter(
                (c) => !categories.find((category) => category.color == c),
            );

            // If there are colors left to pick from, use the first one
            if (colorsLeft.length > 0) {
                color = colorsLeft[0];
            }
        }
    }

    return {
        id: uuidv4(),
        name: preferredName,
        color,
        samples: 0,
    };
};

export type CategoryMessageType =
    'category.rename' | 'category.clear' | 'category.record';

const MediaMachineCategories = ({
    isEmbedded,
    frameRef,
    preferredCategoryNames,
    categories,
    setCategories,
    classifiedCategory,
}: {
    isEmbedded?: boolean;
    frameRef: React.RefObject<HTMLIFrameElement | null>;
    preferredCategoryNames: string[];
    categories: MediaMachineCategory[];
    setCategories: (
        categories:
            | MediaMachineCategory[]
            | ((categories: MediaMachineCategory[]) => MediaMachineCategory[]),
    ) => void;
    classifiedCategory: MediaMachineCategory | null;
}) => {
    const categoriesContainerRef = useRef<HTMLOListElement>(null);

    const [categoryThumbnails, setCategoryThumbnails] = useState<
        Record<string, string[]>
    >({});
    const [maxConnectionsDimensions, setMaxConnectionsDimensions] = useState<{
        width: number;
        height: number;
    }>({ width: 0, height: 0 });

    useEffect(() => {
        setMaxConnectionsDimensions({
            width: categoriesContainerRef.current?.offsetWidth ?? 0,
            height: categoriesContainerRef.current?.offsetHeight ?? 0,
        });
    }, [
        categories.length,
        categoriesContainerRef.current,
        categoriesContainerRef.current?.offsetHeight,
        categoriesContainerRef.current?.offsetWidth,
    ]);

    useEffect(() => {
        if (categories.length == 0) {
            setCategories(
                preferredCategoryNames.map((categoryName, index) =>
                    createNewCategory([], categoryName, index),
                ),
            );
        }
    }, [preferredCategoryNames, categories]);

    const sendMessage = useCallback(
        (type: CategoryMessageType, data: Record<string, unknown>) => {
            frameRef.current?.contentWindow?.postMessage(
                {
                    _hetklokhuisaistudio: true,
                    type,
                    ...data,
                },
                '*',
            );
        },
        [frameRef.current],
    );

    return (
        <div className={styles['mediamachine__categories']}>
            <MediaMachineConnections
                isEmbedded={isEmbedded}
                categories={categories}
                classifiedCategory={classifiedCategory}
                maxWidth={maxConnectionsDimensions.width}
                maxHeight={maxConnectionsDimensions.height}
            />
            <ol
                ref={categoriesContainerRef}
                aria-label="Categorieën"
                className={styles['mediamachine__categories__list']}
            >
                {categories.map((category, index) => (
                    <MediaMachineCameraCategory
                        key={category.id}
                        frameRef={frameRef}
                        isFirstCategoryWithNotEnoughSamples={
                            categories.findIndex((c) => c.samples < 5) === index
                        }
                        isEmbedded={isEmbedded}
                        category={category}
                        setCategoryName={(name) => {
                            setCategories((categories) =>
                                categories.map((c) =>
                                    c.id == category.id ? { ...c, name } : c,
                                ),
                            );
                        }}
                        setCategorySamples={(samples) => {
                            setCategories((categories) =>
                                categories.map((c) =>
                                    c.id == category.id ? { ...c, samples } : c,
                                ),
                            );
                        }}
                        possibleMoves={
                            new Set(
                                ([-1, 1] as const).filter((move) => {
                                    const index = categories.indexOf(category);
                                    const newIndex = index + move;
                                    return (
                                        newIndex >= 0 &&
                                        newIndex < categories.length
                                    );
                                }),
                            )
                        }
                        moveBy={(by) => {
                            const index = categories.indexOf(category);
                            const newIndex = index + by;
                            if (newIndex >= 0 && newIndex < categories.length) {
                                const newCategories = [...categories];
                                newCategories[index] = categories[newIndex];
                                newCategories[newIndex] = categories[index];
                                setCategories(newCategories);
                            }
                        }}
                        sendMessage={sendMessage}
                        thumbnails={categoryThumbnails[category.id] ?? []}
                        setThumbnails={(thumbnails) =>
                            setCategoryThumbnails((categoryThumbnails) => ({
                                ...categoryThumbnails,
                                [category.id]: thumbnails,
                            }))
                        }
                        canBeRemoved={categories.length > 2}
                        removeCategory={() => {
                            setCategories((categories) =>
                                categories.filter((c) => c.id != category.id),
                            );
                        }}
                    />
                ))}
                {!isEmbedded && categories.length < 6 && (
                    <li>
                        <button
                            className={
                                styles[
                                    'mediamachine__categories__list__addbutton'
                                ]
                            }
                            onClick={() => {
                                measure('media_machine_category_added');
                                measure(
                                    `media_machine_category_added_${
                                        categories.length + 1
                                    }`,
                                );
                                setCategories((categories) => [
                                    ...categories,
                                    createNewCategory(categories, ''),
                                ]);
                            }}
                        >
                            <Icon path={mdiPlusCircle} size={2.2} />
                            Nog een categorie toevoegen
                        </button>
                    </li>
                )}
            </ol>
        </div>
    );
};

export default MediaMachine;
